import { aplicarTimestampsPiscante, fasesDoGrupo, fasesDeOutrosGrupos } from './mapaFaseUtils';

const INITIAL_STATE = {
  list: [],
  listMap: [],
  listCont: [],
  pluviometroMapaData: [],
  /** Grupos que o mapa acompanha. Vazio = qualquer grupo aceito. */
  mapaGrupoIds: [],
  /** controlador_id → timestamp do último patch socket (evita HTTP stale sobrescrever cor). */
  mapaPatchTs: {},
  /** false = mapa pausado; descarta atualização em vôo que chega após o pause. */
  mapaLive: false,
};

const MAP_PATCH_GRACE_MS = 10000;
const MAP_OSCILACAO_MS = 2500;

function assinaturaCorFases(fases) {
  if (!Array.isArray(fases) || !fases.length) return '';
  return fases
    .map(
      f =>
        `${f.fase_id}|${f.fase_cor}|${f.mod_piscante}|${f.fase_cor_piscante || ''}|${f.fase_cor_piscante_ms || ''}`,
    )
    .sort()
    .join('\n');
}

/** Piscante chega com `fase_cor = Apagado` — é leitura viva, não degradada. */
function faseTemCorViva(f) {
  return !!f && (!!f.fase_cor_piscante || (!!f.fase_cor && f.fase_cor !== 'Apagado'));
}

function normalizaGrupos(grupos) {
  if (grupos === null || grupos === undefined) return [];
  const lista = Array.isArray(grupos) ? grupos : [grupos];
  const ids = [];
  for (const g of lista) {
    const id = parseInt(g, 10);
    if (id > 0 && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Garante `grupo_fisico_id` nas fases. A resposta já vem escopada pelo grupo
 * pedido, então marcar é correção — e mantém o merge multi-grupo funcionando
 * mesmo com API antiga, que não envia o campo.
 */
function comGrupoMarcado(fases, grupoId) {
  const gid = parseInt(grupoId, 10);
  if (!gid) return fases || [];
  return (fases || []).map(f =>
    parseInt(f.grupo_fisico_id, 10) === gid ? f : { ...f, grupo_fisico_id: gid },
  );
}

/** Grupo fora do conjunto acompanhado — atualização de outra tela/seleção antiga. */
function grupoForaDoEscopo(state, reqGrupo) {
  const ids = state.mapaGrupoIds || [];
  if (!ids.length || !reqGrupo) return false;
  return !ids.includes(parseInt(reqGrupo, 10));
}

/**
 * Volta-e-volta (A→B→A) em janela curta: leitura instável, descarta.
 * Progressão real (verde→amarelo→vermelho) passa, mesmo em menos de 2,5 s —
 * amarelo dura ~3 s e anéis distintos mudam em sequência.
 */
function leituraSuspeitaFlapVerdes(anteriores, incoming) {
  const prevPorFase = new Map(anteriores.map(f => [parseInt(f.fase_id, 10), f]));
  let quebras = 0;
  for (const f of incoming) {
    const prev = prevPorFase.get(parseInt(f.fase_id, 10));
    if (!prev || prev.fase_cor !== 'Verde') continue;
    if (f.fase_cor === 'Amarelo' || (f.fase_cor === 'Apagado' && !f.fase_cor_piscante)) {
      quebras += 1;
    }
  }
  return quebras >= 2;
}

function ehOscilacaoCor(mapaPatchTs, controladorId, sigNovo, now) {
  const lastTs = mapaPatchTs[controladorId];
  if (!lastTs || now - lastTs >= MAP_OSCILACAO_MS) return false;
  const sigPenultima = mapaPatchTs[`${controladorId}_sig_prev`];
  return !!sigPenultima && sigNovo === sigPenultima;
}

function fasesPorControlador(fases) {
  const map = new Map();
  for (const f of fases || []) {
    const id = parseInt(f.controlador_id, 10);
    if (!id) continue;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push(f);
  }
  return map;
}

/** HTTP full snapshot não sobrescreve ctrl recém-atualizado via socket. */
function mergeHttpPreservandoPatches(atual, incoming, mapaPatchTs, authoritative = false) {
  const now = Date.now();
  const incomingPorCtrl = fasesPorControlador(incoming);
  const atualPorCtrl = fasesPorControlador(atual);
  const todosCtrl = new Set([...atualPorCtrl.keys(), ...incomingPorCtrl.keys()]);
  const result = [];

  for (const ctrlId of todosCtrl) {
    const patched = mapaPatchTs[ctrlId] && now - mapaPatchTs[ctrlId] < MAP_PATCH_GRACE_MS;
    if (patched && atualPorCtrl.has(ctrlId)) {
      result.push(...atualPorCtrl.get(ctrlId));
    } else if (incomingPorCtrl.has(ctrlId)) {
      result.push(...incomingPorCtrl.get(ctrlId));
    } else if (!authoritative && atualPorCtrl.has(ctrlId)) {
      result.push(...atualPorCtrl.get(ctrlId));
    }
  }

  return result.length ? result : incoming;
}

/** HTTP não troca cor de um ctrl se leitura oscila (verde↔vermelho) em intervalo curto. */
function mergeHttpEstavelCor(atual, incoming, mapaPatchTs, authoritative = false) {
  const incomingPorCtrl = fasesPorControlador(incoming);
  const atualPorCtrl = fasesPorControlador(atual);
  const now = Date.now();
  const result = [];

  for (const [ctrlId, fasesIn] of incomingPorCtrl.entries()) {
    const fasesAtual = atualPorCtrl.get(ctrlId);
    if (!fasesAtual || !fasesAtual.length) {
      result.push(...fasesIn);
      continue;
    }

    const sigAtual = assinaturaCorFases(fasesAtual);
    const sigIn = assinaturaCorFases(fasesIn);
    if (sigAtual === sigIn) {
      result.push(...fasesIn);
      continue;
    }

    if (ehOscilacaoCor(mapaPatchTs, ctrlId, sigIn, now)) {
      result.push(...fasesAtual);
      continue;
    }

    result.push(...fasesIn);
  }

  if (!authoritative) {
    for (const [ctrlId, fasesAtual] of atualPorCtrl.entries()) {
      if (!incomingPorCtrl.has(ctrlId)) {
        result.push(...fasesAtual);
      }
    }
  }

  return result.length ? result : incoming;
}

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case 'FASE_FETCHED':
      return { ...state, list: action.payload.data };
    case 'FASEMAP_LIVE':
      return { ...state, mapaLive: !!(action.meta && action.meta.ativo) };
    case 'FASEMAP_CLEAR':
      return {
        ...state,
        listMap: [],
        mapaGrupoIds: normalizaGrupos(action.meta && action.meta.grupos),
        mapaPatchTs: {},
      };
    case 'FASEMAP_FETCHED': {
      if (!state.mapaLive) return state;
      const reqGrupo = action.meta && action.meta.grupo;
      if (grupoForaDoEscopo(state, reqGrupo)) {
        return state;
      }
      const incoming = comGrupoMarcado(action.payload.data || [], reqGrupo);

      // Multi-grupo: o snapshot é autoridade só sobre o próprio grupo; as fases
      // dos outros grupos ficam intactas no `listMap`.
      const multiGrupo = (state.mapaGrupoIds || []).length > 1 && !!reqGrupo;
      const listaCompleta = state.listMap || [];
      const outrosGrupos = multiGrupo ? fasesDeOutrosGrupos(listaCompleta, reqGrupo) : [];
      const atual = multiGrupo ? fasesDoGrupo(listaCompleta, reqGrupo) : listaCompleta;
      const replace = action.meta && action.meta.replace;
      const fromHttp = action.meta && action.meta.source === 'http';
      const authoritative = !!(action.meta && action.meta.authoritative) || (replace && fromHttp);

      if (!replace && !authoritative && atual.length > 0 && incoming.length > 0 && incoming.length < atual.length) {
        return state;
      }

      let listMap = incoming;
      const patchTs = state.mapaPatchTs || {};
      const temPatches = Object.keys(patchTs).some(k => /^\d+$/.test(String(k)));

      if (authoritative && atual.length > 0 && incoming.length >= 0) {
        listMap = mergeHttpPreservandoPatches(atual, incoming, patchTs, true);
        listMap = mergeHttpEstavelCor(atual, listMap, patchTs, true);
      } else if (replace && fromHttp && atual.length > 0 && incoming.length > 0) {
        listMap = mergeHttpPreservandoPatches(atual, incoming, patchTs, true);
        listMap = mergeHttpEstavelCor(atual, listMap, patchTs, true);
      } else if (!fromHttp && temPatches && atual.length > 0 && incoming.length > 0) {
        listMap = mergeHttpPreservandoPatches(atual, incoming, patchTs);
      }

      const listMapComTtl = aplicarTimestampsPiscante(
        multiGrupo ? [...outrosGrupos, ...listMap] : listMap,
        listaCompleta,
      );
      return {
        ...state,
        listMap: listMapComTtl,
      };
    }
    case 'FASEMAP_PATCH': {
      if (!state.mapaLive) return state;
      const controladorId = parseInt(action.payload.controladorId, 10);
      const reqGrupo = action.meta && action.meta.grupo;
      if (grupoForaDoEscopo(state, reqGrupo)) {
        return state;
      }
      let incoming = comGrupoMarcado(action.payload.data || [], reqGrupo);
      if (!controladorId || !incoming.length) {
        return state;
      }
      const atual = state.listMap || [];
      const anteriores = atual.filter(f => parseInt(f.controlador_id, 10) === controladorId);
      if (anteriores.length) {
        const prevPorFase = new Map(anteriores.map(f => [parseInt(f.fase_id, 10), f]));
        incoming = incoming.map(f => {
          if (!f.stale || f.fase_cor !== 'Amarelo') return f;
          const prev = prevPorFase.get(parseInt(f.fase_id, 10));
          if (!prev || prev.fase_cor === 'Amarelo') return f;
          return { ...f, fase_cor: prev.fase_cor };
        });
      }
      if (anteriores.length && assinaturaCorFases(anteriores) === assinaturaCorFases(incoming)) {
        return state;
      }
      const tinhaCor = anteriores.some(faseTemCorViva);
      const temCor = incoming.some(faseTemCorViva);
      if (tinhaCor && !temCor) {
        return state;
      }
      if (anteriores.length && temCor && leituraSuspeitaFlapVerdes(anteriores, incoming)) {
        return state;
      }

      const mapaPatchTs = { ...(state.mapaPatchTs || {}) };
      const sigAnterior = assinaturaCorFases(anteriores);
      const newSig = assinaturaCorFases(incoming);
      const now = Date.now();
      if (anteriores.length && ehOscilacaoCor(mapaPatchTs, controladorId, newSig, now)) {
        return state;
      }

      const result = [];
      let inserted = false;
      for (const f of atual) {
        if (parseInt(f.controlador_id, 10) === controladorId) {
          if (!inserted) {
            result.push(...incoming);
            inserted = true;
          }
        } else {
          result.push(f);
        }
      }
      if (!inserted) {
        result.push(...incoming);
      }
      const listMapComTtl = aplicarTimestampsPiscante(result, atual);
      mapaPatchTs[controladorId] = Date.now();
      mapaPatchTs[`${controladorId}_sig_prev`] = sigAnterior;
      mapaPatchTs[`${controladorId}_sig`] = newSig;
      return { ...state, listMap: listMapComTtl, mapaPatchTs };
    }
    case 'FASEMAPCONTROLADORLIST_FETCHED':
      return { ...state, listCont: action.payload.data };
    case 'FASEMAPAINICIO_FETCHED':
      return { ...state, inicioMapa: action.payload.data };
    case 'PLUVIOMETRO_MAPA_FETCHED':
      return { ...state, pluviometroMapaData: action.payload.data };
    default:
      return state;
  }
};
